/**
 * Order Tracking — JSON-only, single source of truth.
 * Fetches ONLY from /orders/{order_number}?email={email}.
 * Timeline, status, and progress stepper from real Shopify data only.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-order-tracking]';

  function parseIsoDate(str) {
    if (!str) return null;
    var d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }

  function formatDate(date, locale) {
    if (!date || !(date instanceof Date)) return '';
    try {
      return date.toLocaleDateString(locale || document.documentElement.lang || 'en', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return date.toISOString();
    }
  }

  function formatDateShort(date, locale) {
    if (!date || !(date instanceof Date)) return '';
    try {
      return date.toLocaleDateString(locale || document.documentElement.lang || 'en', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return date.toISOString();
    }
  }

  function normalizeOrder(data) {
    var order = data && (data.order || data);
    if (!order || typeof order !== 'object') return null;
    var created = order.created_at || order.createdAt;
    var processedAt = order.processed_at || order.processedAt;
    var canceledAt = order.canceled_at || order.canceledAt;
    var financialStatus = (order.financial_status || order.financialStatus || '').toLowerCase();
    var fulfillments = order.fulfillments || [];
    if (!Array.isArray(fulfillments)) fulfillments = [];
    return {
      created_at: created,
      processed_at: processedAt,
      canceled_at: canceledAt,
      financial_status: financialStatus,
      fulfillments: fulfillments.map(function (f) {
        return {
          created_at: f.created_at || f.createdAt,
          fulfillment_status: (f.fulfillment_status || f.status || '').toLowerCase(),
          tracking_company: (f.tracking_company || f.trackingCompany || '').trim(),
          tracking_number: f.tracking_number || f.trackingNumber || '',
          tracking_url: f.tracking_url || f.trackingUrl || ''
        };
      })
    };
  }

  function getProgressStep(order) {
    if (order.canceled_at) return 'cancelled';
    var fulfillments = order.fulfillments || [];
    var anyDelivered = fulfillments.some(function (f) {
      var s = f.fulfillment_status || '';
      return s === 'delivered' || s === 'received';
    });
    if (anyDelivered) return 'delivered';
    if (fulfillments.length > 0) return 'shipped';
    var fs = order.financial_status || '';
    if (fs === 'paid' || fs === 'partially_paid') return 'confirmed';
    return 'placed';
  }

  function getStatusSummary(order, strings) {
    if (order.canceled_at) return strings.cancelled || 'Cancelled';
    if (order.financial_status === 'pending') return strings.awaitingPayment || 'Awaiting payment';
    if (order.fulfillments && order.fulfillments.length > 0) {
      var anyDelivered = order.fulfillments.some(function (f) {
        var s = f.fulfillment_status || '';
        return s === 'delivered' || s === 'received';
      });
      if (anyDelivered) return strings.delivered || 'Delivered';
      return strings.onTheWayToDeliver || 'On the way to deliver';
    }
    return strings.orderConfirmed || 'Order confirmed';
  }

  function getStatusBadgeClass(step) {
    var map = {
      placed: 'order-tracking__progress-badge--pending',
      confirmed: 'order-tracking__progress-badge--confirmed',
      shipped: 'order-tracking__progress-badge--transit',
      delivered: 'order-tracking__progress-badge--delivered',
      cancelled: 'order-tracking__progress-badge--cancelled'
    };
    return map[step] || 'order-tracking__progress-badge--pending';
  }

  function buildTimelineEvents(order, strings) {
    var events = [];
    var created = order.created_at ? parseIsoDate(order.created_at) : null;
    var processedAt = order.processed_at ? parseIsoDate(order.processed_at) : null;
    var financialStatus = order.financial_status || '';
    var fulfillments = order.fulfillments || [];

    if (created) {
      events.push({ key: 'order_placed', label: strings.orderPlaced || 'Order placed', date: created, status: 'completed', icon: 'order' });
    }

    if (financialStatus === 'pending') {
      if (created) {
        events.push({ key: 'payment_pending', label: strings.paymentPending || 'Payment pending', date: created, status: 'current', icon: 'payment' });
      }
    } else if ((financialStatus === 'paid' || financialStatus === 'partially_paid') && processedAt) {
      events.push({ key: 'payment_confirmed', label: strings.paymentConfirmed || 'Payment confirmed', date: processedAt, status: 'completed', icon: 'payment' });
    }

    if (fulfillments.length > 0) {
      fulfillments.forEach(function (f, idx) {
        var fulfilledAt = f.created_at ? parseIsoDate(f.created_at) : null;
        if (!fulfilledAt) return;
        var company = (f.tracking_company || '').trim();
        var isLeopards = company.toLowerCase().indexOf('leopards') !== -1;
        var courierLabel = isLeopards ? (strings.leopardsCourier || 'Leopards Courier') : (company || 'Courier');
        events.push({ key: 'fulfilled_' + idx, label: strings.orderFulfilled || 'Order fulfilled', date: fulfilledAt, status: 'completed', icon: 'fulfillment' });
        if (company || f.tracking_number) {
          events.push({
            key: 'shipped_' + idx,
            label: (strings.shippedVia || 'Shipped via') + ' ' + courierLabel,
            date: fulfilledAt,
            status: isLeopards ? 'in_transit' : 'completed',
            icon: 'truck',
            tracking_number: f.tracking_number,
            tracking_url: f.tracking_url,
            tracking_company: company,
            is_leopards: isLeopards
          });
        }
      });
    }

    events.sort(function (a, b) {
      return (a.date ? a.date.getTime() : 0) - (b.date ? b.date.getTime() : 0);
    });
    return events;
  }

  function escapeHtml(s) {
    if (s == null) return '';
    var div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  function escapeAttr(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function updateProgressStepper(sectionEl, step, statusLabel) {
    var stepsContainer = sectionEl.querySelector('[data-order-tracking-steps]');
    if (!stepsContainer) return;
    var stepOrder = ['placed', 'confirmed', 'shipped', 'delivered'];
    var currentIdx = stepOrder.indexOf(step);

    var stepEls = stepsContainer.querySelectorAll('[data-step]');
    stepEls.forEach(function (el) {
      var elStep = el.getAttribute('data-step');
      var elIdx = stepOrder.indexOf(elStep);
      el.classList.remove('order-tracking__step--active', 'order-tracking__step--completed');
      if (step === 'cancelled') return;
      if (elIdx < currentIdx) {
        el.classList.add('order-tracking__step--completed');
      } else if (elIdx === currentIdx) {
        el.classList.add('order-tracking__step--active');
      }
    });

    var badge = sectionEl.querySelector('[data-order-tracking-status-badge]');
    if (badge) {
      badge.className = 'order-tracking__progress-badge ' + getStatusBadgeClass(step);
      var statusText = badge.querySelector('[data-shipment-status]');
      if (statusText) statusText.textContent = statusLabel;
    }
  }

  function renderTimeline(events, container, ctaContainer, strings) {
    if (!container) return;
    container.innerHTML = '';
    var locale = document.documentElement.lang || 'en';

    events.forEach(function (ev) {
      var statusClass = 'order-tracking__badge--' + (ev.status || 'completed');
      var statusLabel = ev.status === 'in_transit'
        ? (strings.inTransit || 'In transit')
        : (ev.status === 'current' ? (strings.paymentPending || 'Pending') : '');
      var badgeHtml = statusLabel
        ? '<span class="order-tracking__badge ' + statusClass + '">' + escapeHtml(statusLabel) + '</span>'
        : '';

      var trackingHtml = '';
      if (ev.tracking_number) {
        trackingHtml = '<p class="order-tracking__timeline-tracking" data-tracking-copy="' + escapeAttr(ev.tracking_number) + '" title="Click to copy">' + escapeHtml(ev.tracking_number) + '</p>';
      }

      var card = document.createElement('div');
      card.className = 'order-tracking__timeline-card';
      card.setAttribute('data-event', ev.key);
      card.innerHTML =
        '<div class="order-tracking__timeline-item">' +
          '<div class="order-tracking__timeline-marker"></div>' +
          '<div class="order-tracking__timeline-content order-tracking__timeline-card-inner">' +
            '<div class="order-tracking__timeline-header">' +
              '<span class="order-tracking__timeline-label">' + escapeHtml(ev.label) + '</span>' + badgeHtml +
            '</div>' +
            (ev.date ? '<time class="order-tracking__timeline-date" datetime="' + (ev.date.toISOString ? ev.date.toISOString() : '') + '">' + formatDate(ev.date, locale) + '</time>' : '') +
            trackingHtml +
          '</div>' +
        '</div>';
      container.appendChild(card);
    });

    if (ctaContainer && events.length > 0) {
      var trackingEvents = events.filter(function (e) { return e.tracking_url; });
      if (trackingEvents.length > 0) {
        var first = trackingEvents[0];
        var ctaLabel = strings.trackWithCourier || 'Track with courier';
        var href = first.tracking_url;
        try {
          if (href && href.indexOf('http') !== 0) href = new URL(href, window.location.origin).href;
        } catch (e) {}
        ctaContainer.innerHTML =
          '<a href="' + escapeAttr(href || first.tracking_url) + '" class="button button--primary order-tracking__cta" target="_blank" rel="noopener noreferrer" is="hover-button">' +
            '<span class="btn-fill" data-fill></span>' +
            '<span class="btn-text">' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>' +
              escapeHtml(ctaLabel) +
            '</span>' +
          '</a>';
      } else {
        ctaContainer.innerHTML = '';
      }
    }
  }

  function showCopyToast(sectionEl) {
    var toast = sectionEl.querySelector('[data-copy-toast]');
    if (!toast) return;
    toast.classList.add('is-visible');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 1800);
  }

  function setupCopyHandlers(sectionEl) {
    sectionEl.addEventListener('click', function (e) {
      var el = e.target.closest('[data-tracking-copy]');
      if (!el) return;
      var text = el.getAttribute('data-tracking-copy');
      if (!text) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function () {
          showCopyToast(sectionEl);
        });
      } else {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;left:-9999px;opacity:0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); showCopyToast(sectionEl); } catch (e) {}
        document.body.removeChild(ta);
      }
    });
  }

  function showLoading(loadingEl, errorEl, timelineWrap) {
    if (loadingEl) loadingEl.hidden = false;
    if (errorEl) errorEl.hidden = true;
    if (timelineWrap) timelineWrap.hidden = true;
  }

  function showError(loadingEl, errorEl, timelineWrap, message) {
    if (loadingEl) loadingEl.hidden = true;
    if (timelineWrap) timelineWrap.hidden = true;
    if (errorEl) {
      errorEl.hidden = false;
      var text = errorEl.querySelector('[data-order-tracking-error-text]');
      if (text) text.textContent = message || '';
    }
  }

  function showTimeline(loadingEl, errorEl, timelineWrap) {
    if (loadingEl) loadingEl.hidden = true;
    if (errorEl) errorEl.hidden = true;
    if (timelineWrap) timelineWrap.hidden = false;
  }

  /**
   * Theme floating labels use :not(:placeholder-shown). With placeholder=" ",
   * some browsers/autofill keep the label overlapping the value — clear placeholder when filled.
   */
  function syncFloatingPlaceholder(input) {
    if (!input || !input.hasAttribute('data-floating-placeholder')) return;
    if (String(input.value || '').length > 0) {
      input.removeAttribute('placeholder');
    } else {
      input.setAttribute('placeholder', ' ');
    }
  }

  function setupFloatingPlaceholders(sectionEl) {
    var inputs = sectionEl.querySelectorAll('[data-floating-placeholder]');
    function bind(input) {
      function onChange() {
        syncFloatingPlaceholder(input);
      }
      input.addEventListener('input', onChange);
      input.addEventListener('change', onChange);
      input.addEventListener('blur', onChange);
      syncFloatingPlaceholder(input);
    }
    for (var i = 0; i < inputs.length; i++) {
      bind(inputs[i]);
    }
    setTimeout(function () {
      for (var j = 0; j < inputs.length; j++) {
        syncFloatingPlaceholder(inputs[j]);
      }
    }, 150);
  }

  function runSection(sectionEl) {
    var form = sectionEl.querySelector('[data-order-tracking-form]');
    var resultEl = sectionEl.querySelector('[data-order-tracking-result]');
    var loadingEl = sectionEl.querySelector('[data-order-tracking-loading]');
    var errorEl = sectionEl.querySelector('[data-order-tracking-error]');
    var timelineWrap = sectionEl.querySelector('[data-order-tracking-timeline]');
    var shipmentCard = sectionEl.querySelector('[data-order-tracking-shipment-card]');
    var timelineList = sectionEl.querySelector('[data-order-tracking-timeline-list]');
    var ctaEl = sectionEl.querySelector('[data-order-tracking-cta]');
    var retryBtn = sectionEl.querySelector('[data-order-tracking-retry]');

    var lookupUrlTemplate = (sectionEl.dataset.lookupUrl || '').trim();
    var strings = {};
    try {
      strings = JSON.parse(sectionEl.dataset.strings || '{}');
    } catch (e) {}

    setupCopyHandlers(sectionEl);

    function getOrderNumber() {
      var input = sectionEl.querySelector('[data-order-number]');
      return input ? String(input.value || '').trim().replace(/^#/, '') : '';
    }

    function getEmail() {
      var input = sectionEl.querySelector('[data-order-email]');
      return input ? String(input.value || '').trim() : '';
    }

    function buildLookupUrl(orderNumber, email) {
      var params = 'email=' + encodeURIComponent(email);
      if (lookupUrlTemplate) {
        var url = lookupUrlTemplate.replace(/\{order_number\}/g, encodeURIComponent(orderNumber));
        return (url.indexOf('?') !== -1 ? url + '&' : url + '?') + params;
      }
      return '/orders/' + encodeURIComponent(orderNumber) + '?' + params;
    }

    function displayResult(orderNumber, order, events, strings) {
      var dateStr = order.created_at ? formatDateShort(parseIsoDate(order.created_at), document.documentElement.lang) : '';
      var statusLabel = getStatusSummary(order, strings);
      var step = getProgressStep(order);

      updateProgressStepper(sectionEl, step, statusLabel);

      if (shipmentCard) {
        var numEl = shipmentCard.querySelector('[data-shipment-number]');
        var dateEl = shipmentCard.querySelector('[data-shipment-date]');
        if (numEl) numEl.textContent = '#' + orderNumber;
        if (dateEl) dateEl.textContent = dateStr || '—';
        var statusWrap = shipmentCard.querySelector('[data-shipment-status-wrap]');
        var statusText = shipmentCard.querySelector('[data-shipment-status-text]');
        if (statusWrap && statusText) {
          statusWrap.hidden = false;
          statusText.textContent = statusLabel;
        }
      }

      renderTimeline(events, timelineList, ctaEl, strings);
      showTimeline(loadingEl, errorEl, timelineWrap);
      if (timelineWrap) timelineWrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function doLookup(orderNumber, email) {
      var btn = sectionEl.querySelector('[data-track-button]');
      function endButtonBusy() {
        if (!btn) return;
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        btn.style.opacity = '';
      }

      if (!orderNumber || !email) {
        endButtonBusy();
        return;
      }

      resultEl.hidden = false;
      showLoading(loadingEl, errorEl, timelineWrap);

      var url = buildLookupUrl(orderNumber, email);
      var notFoundMsg = strings.notFound || 'Order not found.';

      if (btn) {
        btn.disabled = true;
        btn.style.opacity = '0.7';
      }

      console.log('[OrderTracking] Fetching:', url);
      fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (res) {
          var contentType = (res.headers.get('content-type') || '').toLowerCase();
          console.log('[OrderTracking] Response status:', res.status, '| Content-Type:', contentType);
          if (!res.ok) {
            return res.text().then(function (body) {
              console.error('[OrderTracking] Non-OK response body:', body.substring(0, 500));
              showError(loadingEl, errorEl, timelineWrap, notFoundMsg);
              return null;
            });
          }
          if (contentType.indexOf('application/json') === -1) {
            return res.text().then(function (body) {
              console.error('[OrderTracking] Non-JSON content-type. Body:', body.substring(0, 500));
              showError(loadingEl, errorEl, timelineWrap, notFoundMsg);
              return null;
            });
          }
          return res.json();
        })
        .then(function (data) {
          if (data == null) return;
          console.log('[OrderTracking] Parsed JSON data:', JSON.stringify(data).substring(0, 1000));
          var order = normalizeOrder(data);
          if (!order) {
            console.error('[OrderTracking] normalizeOrder returned null — unexpected data shape');
            showError(loadingEl, errorEl, timelineWrap, notFoundMsg);
            return;
          }
          console.log('[OrderTracking] Normalized order:', JSON.stringify(order).substring(0, 500));
          var events = buildTimelineEvents(order, strings);
          if (events.length === 0 && order.created_at) {
            events.push({ key: 'order_placed', label: strings.orderPlaced || 'Order placed', date: parseIsoDate(order.created_at), status: 'completed', icon: 'order' });
          }
          displayResult(orderNumber, order, events, strings);
        })
        .catch(function (err) {
          console.error('[OrderTracking] Fetch error:', err);
          showError(loadingEl, errorEl, timelineWrap, notFoundMsg);
        })
        .finally(function () {
          endButtonBusy();
        });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        doLookup(getOrderNumber(), getEmail());
      });
    }

    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        doLookup(getOrderNumber(), getEmail());
      });
    }

    var params = new URLSearchParams(window.location.search);
    var qOrder = params.get('order_number');
    var qEmail = params.get('email');
    if (qOrder && qEmail) {
      var numInput = sectionEl.querySelector('[data-order-number]');
      var emailInput = sectionEl.querySelector('[data-order-email]');
      if (numInput) {
        numInput.value = qOrder;
        syncFloatingPlaceholder(numInput);
      }
      if (emailInput) {
        emailInput.value = qEmail;
        syncFloatingPlaceholder(emailInput);
      }
      doLookup(String(qOrder).trim().replace(/^#/, ''), String(qEmail).trim());
    }
  }

  function init() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) {
      runSection(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
